import { Box, Button, Stack, Typography } from '@mui/material'
import { ColDef } from 'ag-grid-community'
import { deleteUser } from 'api/users'
import ConfirmRemoveModal from 'components/modals/ConfirmRemoveModal'
import LoadingPlaceholder from 'components/placeholders/LoadingPlaceholder'
import InfinityAgGrid from 'components/table/InfinityAgGrid'
import { getActionColumn } from 'features/UI'
import { useRemoveModalControl } from 'hooks/useRemoveModalControl'
import { useShowControl } from 'hooks/useShowControl'
import { useMemo } from 'react'
import { UserInfoInOrganization } from 'types'
import { UserOrgRolesAddUserModal } from '../UserOrgRolesAddUserModal/UserOrgRolesAddUserModal'

export function UserOrgRolesUnassignedUsers({
  data,
  isLoading,
  onRefresh,
  organizationId,
}: {
  isLoading: boolean
  data: UserInfoInOrganization[]
  onRefresh: () => void
  organizationId?: number
}) {
  const [isOpenAddModal, onOpenAddModal, onCloseAddModal] = useShowControl()

  const { idToRemove, openDeleteModal, closeDeleteModal, removeLoading, handleConfirmRemove } =
    useRemoveModalControl({
      deleteFunc: deleteUser,
      successCallback: () => onRefresh(),
      warning: "Can't remove user",
    })

  const actions = useMemo(() => {
    return [{ name: 'Delete', onClick: openDeleteModal }]
  }, [openDeleteModal])

  const columnDefs: ColDef[] = useMemo(() => {
    return [
      { headerName: 'First Name', field: 'firstname' },
      { headerName: 'Last Name', field: 'lastname' },
      { headerName: 'Email', field: 'email', flex: 1 },
      { ...getActionColumn(actions) },
    ]
  }, [actions])

  return (
    <Stack gap={2} mt={4}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography fontWeight={600}>Unassigned Users</Typography>
        <Button variant="contained" type="button" onClick={onOpenAddModal}>
          Add User
        </Button>
      </Stack>

      <Box>
        {isLoading ? (
          <LoadingPlaceholder />
        ) : (
          <InfinityAgGrid
            columnDefs={columnDefs}
            rowModelType="clientSide"
            domLayout="autoHeight"
            rowData={data}
            floatingFiltersHeight={0}
          />
        )}
      </Box>

      <UserOrgRolesAddUserModal
        isOpen={isOpenAddModal}
        onClose={onCloseAddModal}
        onAfterSubmit={onRefresh}
        organizationId={organizationId}
      />

      <ConfirmRemoveModal
        entityToRemove="User"
        loading={removeLoading}
        isOpen={idToRemove !== null}
        handleConfirm={handleConfirmRemove}
        handleClose={closeDeleteModal}
      />
    </Stack>
  )
}
